import {
  BreakoutRoomWithUserEntries,
  BreakoutRoomWithUserIds,
  NormalizedBreakoutRoomsConfig,
  NormalizedRoomsState,
} from 'features/breakout-rooms/types';
import { call, put, select } from 'redux-saga/effects';
import { selectLocalUser, selectUsers } from 'features/users/usersSlice';
import { User, UserEntries } from 'features/users/types';
import { normalizeRooms } from 'features/breakout-rooms/utils/normalizeRooms';
import { roomsConfigured } from 'features/breakout-rooms/breakoutRoomsSlice';
import { mapUserEntriesToRoom } from 'features/breakout-rooms/utils/mapUserEntriesToRoom';
import { mapUnassignedUsers } from 'features/breakout-rooms/utils/mapUnassignedUsers';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';

// Checks if the provided array of rooms is an array of BreakoutRoomWithUserIds.
function isRoomsWithUserIds(
  rooms: Array<BreakoutRoomWithUserIds | BreakoutRoomWithUserEntries>
): rooms is BreakoutRoomWithUserIds[] {
  const roomWithUsers = rooms.find((room) => room.users.length > 0);
  if (!roomWithUsers) {
    return false;
  }

  return typeof roomWithUsers.users[0] === 'string';
}

export function* normalizeBreakoutRoomsSaga({
  rooms,
  unassignedUsers,
}: {
  rooms: Array<BreakoutRoomWithUserEntries | BreakoutRoomWithUserIds>;
  unassignedUsers?: User[];
}) {
  if (rooms.length === 0) {
    return;
  }

  const allUserEntries: UserEntries = yield select(selectUsers);

  const roomsWithUserEntries: BreakoutRoomWithUserEntries[] = isRoomsWithUserIds(rooms)
    ? yield call(mapUserEntriesToRoom, rooms, allUserEntries)
    : rooms;

  const normalizedState: NormalizedRoomsState = yield call(normalizeRooms, roomsWithUserEntries);

  const configurationPayload: NormalizedBreakoutRoomsConfig = {
    normalizedState,
    unassignedUsers: mapUnassignedUsers(allUserEntries, normalizedState.users),
  };

  if (unassignedUsers) {
    for (const user of unassignedUsers) {
      configurationPayload.unassignedUsers[user.id] = user;
    }
  }

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  if (e2eeEnabled) {
    // sync local user name
    const localUser: User | undefined = yield select(selectLocalUser);

    if (localUser) {
      if (configurationPayload.normalizedState.users[localUser.id]) {
        configurationPayload.normalizedState.users[localUser.id] = localUser;
      } else {
        configurationPayload.unassignedUsers[localUser.id] = localUser;
      }
    }
  }

  yield put(roomsConfigured(configurationPayload));

  return configurationPayload;
}
